<script lang="ts">
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import type { InlineTypographyComponentProps } from './types';

  type $$Props = InlineTypographyComponentProps;

  export let color: InlineTypographyComponentProps['color'];
  export let style: InlineTypographyComponentProps['style'];
  export let entryId: InlineTypographyComponentProps['entryId'];
  export let cssOverrides: InlineTypographyComponentProps['cssOverrides'];
  export let inlineTexts: InlineTypographyComponentProps['inlineTexts'];
  export let customCssClass: InlineTypographyComponentProps['customCssClass'];
  export let balanceText: InlineTypographyComponentProps['balanceText'];

  const getCssProperty = (property: string) => cssOverrides && cssOverrides[property];
</script>

<span
  class="InlineTypographyComponent {customCssClass}"
  data-entry-id={entryId}
  style:--color={getCssProperty('color') ?? (color ? `var(--color-${color})` : '')}
  class:font-size-tiny={style === 'font-size-tiny'}
  class:font-size-small={style === 'font-size-small'}
  class:font-size-medium={style === 'font-size-medium'}
  class:font-size-large={style === 'font-size-large'}
  class:legal-text={style === 'legal-text'}
  class:font-eyebrow-large={style === 'font-eyebrow-large'}
  class:font-eyebrow-small={style === 'font-eyebrow-small'}
  class:pull-quote={style === 'pull-quote'}
  class:upperspaced={style === 'font-upperspaced'}
  class:title-xl={style === 'title-xl'}
  class:h1={style === 'h1'}
  class:h2={style === 'h2'}
  class:h3={style === 'h3'}
  class:h4={style === 'h4'}
  class:h5={style === 'h5'}
  class:h6={style === 'h6'}
  class:balance-text={balanceText}
  class:badge-pricing-tier={style === 'badge-pricing-tier'}
  style:--font-size={getCssProperty('font-size')}
  style:--font-weight={getCssProperty('font-weight')}
  style:--text-align={getCssProperty('text-align')}
  style:--text-decoration={getCssProperty('text-decoration')}
  style:--line-height={getCssProperty('line-height')}
  style:--letter-spacing={getCssProperty('letter-spacing')}
  style:--word-spacing={getCssProperty('word-spacing')}
  style:--text-transform={getCssProperty('text-transform')}
  style:--font-style={getCssProperty('font-style')}
  style:--text-shadow={getCssProperty('text-shadow')}
  style:--font-variant={getCssProperty('font-variant')}
  style:--white-space={getCssProperty('white-space')}
  style:--text-overflow={getCssProperty('text-overflow')}
  style:--text-indent={getCssProperty('text-indent')}
  style:--vertical-align={getCssProperty('vertical-align')}
  style:--word-break={getCssProperty('word-break')}
  style:--hyphens={getCssProperty('hyphens')}
  style:--text-rendering={getCssProperty('text-rendering')}
  style:--font-kerning={getCssProperty('font-kerning')}
  style:--direction={getCssProperty('direction')}
  style:--writing-mode={getCssProperty('writing-mode')}
  style:--text-orientation={getCssProperty('text-orientation')}
  style:--text-justify={getCssProperty('text-justify')}
  style:--line-break={getCssProperty('line-break')}
  style:display={getCssProperty('display')}
>
  {#each Object.values(inlineTexts) as text}
    <DynamicChildrenComponent children={[text]} />
  {/each}
</span>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/typography.scss' as typography;

  @layer reset.standard-components {
    :global {
      .InlineTypographyComponent,
      .InlineTypographyComponent * {
        font-family: var(--font-family);
        font-size: var(--font-size);
        line-height: var(--line-height);
        letter-spacing: var(--letter-spacing);
        font-weight: var(--font-weight);
        color: var(--color);
        text-align: var(--text-align);
        text-indent: var(--text-indent);
        vertical-align: var(--vertical-align);
        word-break: var(--word-break);
        hyphens: var(--hyphens);
        text-rendering: var(--text-rendering);
        font-kerning: var(--font-kerning);
        direction: var(--direction);
        writing-mode: var(--writing-mode);
        text-orientation: var(--text-orientation);
        line-break: var(--line-break);
        text-decoration: var(--text-decoration);
        word-spacing: var(--word-spacing);
        text-transform: var(--text-transform);
        font-style: var(--font-style);
        text-shadow: var(--text-shadow);
        font-variant: var(--font-variant);
        white-space: var(--white-space);
        transition: var(--transition);
      }
    }
  }

  @layer standard-components {
    .InlineTypographyComponent {
      &.title-xl {
        @include typography.title-xl;
      }
      &.font-size-tiny {
        @include typography.body-extra-small;
      }
      &.font-size-small {
        @include typography.body-small;
      }
      &.font-size-base {
        @include typography.body-small;
      }
      &.font-size-medium {
        @include typography.body;
      }
      &.font-size-large {
        @include typography.body-large;
      }
      &.font-eyebrow {
        @include typography.eyebrow;
      }
      &.font-eyebrow-large {
        @include typography.eyebrow;
      }
      &.font-eyebrow-small {
        @include typography.eyebrow;
      }
      &.badge-pricing-tier {
        --font-size: var(--font-size-14);
        font-family: var(--font-family-base);
        font-size: var(--font-size);
        font-weight: var(--font-weight-semibold);
        letter-spacing: normal;
        line-height: 1;

        // Colors come from Market, currently undefined in PW
        --badge-pricing-tier-background-color: #cce1ff;
        --badge-pricing-tier-color: #005ad9;
        background-color: var(--badge-pricing-tier-background-color);
        color: var(--badge-pricing-tier-color);

        // Badge shape
        padding: 4px 8px;
        border-radius: 100px;

        // Align with adjacent text baseline
        position: relative;
        bottom: 5px;
      }
    }
  }
</style>
